import React, { useState, useEffect, useCallback } from 'react';
import { fetchPrograms, createProgram, fetchProgramById, updateProgram } from './services/program.service';
import { CONTENT_DELIVERY_METHOD, SNACK_BAR_ERROR, SNACK_BAR_SUCCESS } from '../../services/constants';
import store from '../../core/store';
import { Button } from '../../shared/components/MaterialUIComponents';
import useUnleashHook from '../../core/unleash/useUnleashHook';
import UnleashService from '../../services/unleash/unleash.service';

/**
 * @typedef {Object} Program
 * @property {string|number} id - Unique identifier for the program
 * @property {string} name - Name of the program
 * @property {string} deliveryMethod - Method of program delivery
 * @property {string} updatedBy - User who last updated the program
 * @property {string} updatedDate - Date of last update
 */

/**
 * @typedef {Object} ProgramState
 * @property {Array<Program>} programs - List of programs
 * @property {Program|null} programData - Currently selected program data
 * @property {boolean} loading - Loading state
 * @property {string|null} error - Error message if any
 * @property {Date|null} lastRefreshed - Last refresh timestamp
 * @property {number} pageSize - Number of items per page
 */

/**
 * @typedef {Object} UIState
 * @property {boolean} showAddProgram - Whether to show add program form
 * @property {boolean} showEditProgram - Whether to show edit program form
 */

//Unleash

// Constants
const INITIAL_PAGE_SIZE = 50;
const DEFAULT_COLUMNS = [
  {
    name: 'id',
    displayName: 'ID',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
    sortByProp: 'id',
  },
  {
    name: 'programName',
    displayName: 'Program Name',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
    sortByProp: 'name'
  },
  {
    name: 'deliveryMethod',
    displayName: 'Delivery Method',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'updatedBy',
    displayName: 'Updated By',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'updatedDate',
    displayName: 'Updated Date',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'actions',
    displayName: 'Actions',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'override',
  }
];

// Utility functions
/**
 * Formats a timestamp into a "Last Refreshed HH:MM AM/PM" string
 * @param {Date} timestamp - The timestamp to format
 * @returns {string} Formatted time string or empty string if no timestamp
 */
const formatLastRefreshedTime = (timestamp) => {
  if (!timestamp) return '';
  const options = { hour: 'numeric', minute: '2-digit', hour12: true };
  return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', options)}`;
};

/**
 * Maps program data to the required row format for DataGrid
 * @param {Array} programs - Array of program objects
 * @param {Function} onEditProgram - Callback function for edit button click
 * @param {boolean} isNewUI - Flag to determine if new UI format is required
 * @returns {Array} Mapped array of programs with required DataGrid structure
 */
const mapProgramsToRows = (programs, onEditProgram, isNewUI) => {
  if (!Array.isArray(programs)) {
    console.error('Expected programs to be an array, got:', programs);
    return [];
  }
  
  // Sort programs by updatedDate in descending order (newest first)
  const sortedPrograms = [...programs].sort((a, b) => {
    const dateA = new Date(a.updatedDate || 0);
    const dateB = new Date(b.updatedDate || 0);
    return dateB - dateA;
  });

  if (!isNewUI) {
    // Return simple array for legacy UI
    return sortedPrograms;
  }
  
  // Return formatted data for new UI
  return sortedPrograms.map(program => ({
    isSelected: false,
    rowData: {
      id: program.id,
      programName: program.name,
      deliveryMethod: program.deliveryMethod || '',
      updatedBy: program.updatedBy || '',
      updatedDate: program.updatedDate ? new Date(program.updatedDate).toISOString().split('T')[0] : '',
      actions: () => (
        <Button
          color="primary"
          variant="contained"
          aria-label="Edit Program"
          onClick={(e) => onEditProgram(e, program.id)}
        >
          Edit
        </Button>
      )
    },
  }));
};

/**
 * Custom hook for managing programs functionality
 * @returns {Object} Object containing program state and handler functions
 */
const useManageProgramsHook = () => {
  //unleash
  const shouldShowManageProgramsNewUI = useUnleashHook(UnleashService.FLAGS.SHOW_MANAGE_PROGRAMS_LISTING_NEW_UI).isFlagEnabled();

  // State management
  const [programState, setProgramState] = useState({
    programs: [],
    programData: null,
    loading: true,
    error: null,
    lastRefreshed: null,
    pageSize: INITIAL_PAGE_SIZE
  });

  const [uiState, setUiState] = useState({
    showAddProgram: false,
    showEditProgram: false
  });

  // UI Handlers
  /**
   * Scrolls the main container to the top
   */
  const moveScrollToTop = () => {
    const mainContainer = document.getElementById('main-container');
    mainContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // API Handlers

  /**
   * Fetches program details by ID and populates edit form
   * @async
   * @param {Event} e - Click event object
   * @param {string|number} programId - ID of the program to edit
   */
  const getProgramAndPopulateEditFields = useCallback(async (e, programId) => {
    try {
      e.preventDefault();
      setProgramState(prev => ({ ...prev, loading: true }));
      
      const response = await fetchProgramById(programId);
      setProgramState(prev => ({ 
        ...prev, 
        programData: response?.payload,
        loading: false 
      }));
      
      setUiState({
        showEditProgram: true,
        showAddProgram: true
      });
      
      moveScrollToTop();
    } catch (err) {
      console.error(err);
      setProgramState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Function to fetch programs list
  /**
   * Fetches programs list from the API and updates state
   * @async
   * @returns {Promise<void>}
   */
  const getPrograms = useCallback(async () => {
    try {
      setProgramState(prev => ({ ...prev, loading: true }));
      const response = await fetchPrograms(Object.values(CONTENT_DELIVERY_METHOD).join(','));
      const programsList = Array.isArray(response?.payload?.programs) ? response.payload.programs : [];
      setProgramState(prev => ({
        ...prev,
        programs: mapProgramsToRows(programsList, getProgramAndPopulateEditFields, shouldShowManageProgramsNewUI),
        lastRefreshed: new Date(),
        loading: false
      }));
    } catch (err) {
      console.error(err);
      setProgramState(prev => ({
        ...prev,
        error: 'Failed to fetch programs',
        programs: [],
        loading: false
      }));
    }
  }, [shouldShowManageProgramsNewUI, getProgramAndPopulateEditFields]);

  // Initial data fetch
  useEffect(() => {
    getPrograms();
  }, [getPrograms]);

  /**
   * Handles navigation back to program list view
   */
  const handleBackToList = () => {
    setUiState({
      showAddProgram: false,
      showEditProgram: false
    });
    moveScrollToTop();
  };

  // Toast Handler
  /**
   * Shows toast message using global store
   * @param {string} type - Type of toast message (success/error)
   * @param {string} message - Message to display
   */
  const showToastMessage = (type, message) => {
    store.dispatch({ type: 'CHANGE_TOAST_TYPE', payload: type });
    store.dispatch({ type: 'SHOW_TOAST', payload: message });
  };

  /**
   * Handles creating new program or showing add program form
   * @async
   * @param {Object|null} programData - Program data to create, null to show form
   */
  const handleAddNew = async (programData) => {
    if (!programData) {
      setUiState({ showAddProgram: true, showEditProgram: false });
      return;
    }

    try {
      setProgramState(prev => ({ ...prev, loading: true }));
      const response = await createProgram(programData);

      if (response.status === 'SUCCESS') {
        await getPrograms(); // Refresh the list
        handleBackToList();
        showToastMessage(SNACK_BAR_SUCCESS, "Program created successfully");
      } else {
        const errorMsg = response.payload?.errorDetails || 'Failed to create program';
        setProgramState(prev => ({ ...prev, error: 'Failed to create program' }));
        showToastMessage(SNACK_BAR_ERROR, errorMsg);
      }
    } catch (err) {
      console.error(err);
      showToastMessage(SNACK_BAR_ERROR, 'Failed to create program');
    } finally {
      setProgramState(prev => ({ ...prev, loading: false }));
    }
  };

  /**
   * Handles updating existing program
   * @async
   * @param {Object} programData - Updated program data
   * @param {string|number} programId - ID of the program to update
   */
  const handleEditProgram = async (programData, programId) => {
    try {
      setProgramState(prev => ({ ...prev, loading: true }));
      const response = await updateProgram(programData, programId);
      
      if (response?.status === 'success') {
        await getPrograms(); // Refresh the list
        handleBackToList();
      } else {
        setProgramState(prev => ({ ...prev, error: 'Failed to create program' }));
        showToastMessage(SNACK_BAR_ERROR, 'Failed to update program');
      }
    } catch (err) {
      console.error(err);
      showToastMessage(SNACK_BAR_ERROR, 'Failed to update program');
    } finally {
      setProgramState(prev => ({ ...prev, loading: false }));
    }
  };

  return {
    // Data
    programs: programState.programs,
    programData: programState.programData,
    loading: programState.loading,
    error: programState.error,
    lastRefreshed: programState.lastRefreshed,
    pageSize: programState.pageSize,
    
    // UI State
    showAddProgram: uiState.showAddProgram,
    showEditProgram: uiState.showEditProgram,
    
    // Handlers
    handleAddNew,
    handleBackToList,
    handleEditProgram,
    refreshPrograms: getPrograms,
    getProgramAndPopulateEditFields,
    
    // Utilities
    formatLastRefreshedTime,
    columns: DEFAULT_COLUMNS,
    mapProgramsToRows,
    shouldShowManageProgramsNewUI
  };
};

export default useManageProgramsHook;
